import React, {useCallback, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
    BtnBold,
    BtnBulletList,
    BtnClearFormatting,
    BtnItalic,
    BtnLink,
    BtnNumberedList,
    BtnRedo,
    BtnStrikeThrough,
    BtnStyles,
    BtnUnderline,
    BtnUndo, createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Separator,
    Toolbar,
} from 'react-simple-wysiwyg';
import {Box} from '@mui/material';
import {FORM_ACCESS_LEVELS} from '../../../../../../constants/formAccessLevels';
import ColorPicker from './ColorPicker';
import ToggleColorPickerButton from './BtnToogleColorPicker';
import { useTheme } from '@mui/material/styles';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

const schoolPlaceHolder = [
    ['School Name', 'InsertText', '@SchoolName ']
];

const placeholders = [
    ['Parent', 'InsertText', '@Parent '],
    ['Parent Surname', 'InsertText', '@ParentSurname '],
    ['Student Forename', 'InsertText', '@StudentForename '],
    ['Student Full Name', 'InsertText', '@StudentFullName '],
    ['His/Her', 'InsertText', '@HisHer '],
    ['He/She', 'InsertText', '@HeShe '],
    ['Him/Her', 'InsertText', '@HimHer '],
    ['Title', 'InsertText', '@Title '],
    ['Employee', 'InsertText', '@Employee '],
];

const PrivatePlaceholderDropdown = createDropdown('Placeholders',
    [...schoolPlaceHolder, ...placeholders]);

const PublicPlaceholderDropdown = createDropdown('Placeholders', schoolPlaceHolder);

const BtnAlignCenter = createButton('Align center', <FormatAlignCenterIcon fontSize={'small'} sx={{marginTop: '3px'}}/>, 'justifyCenter');
const BtnAlignLeft = createButton('Align left', <FormatAlignLeftIcon fontSize={'small'} sx={{marginTop: '3px'}}/>, 'justifyLeft');
const BtnAlignRight = createButton('Align right', <FormatAlignRightIcon fontSize={'small'} sx={{marginTop: '3px'}}/>, 'justifyRight');

const RichEditor = ({
                        index,
                        value,
                        currentDescription,
                        setCurrentDescription,
                        isFormLive,
                        accessLevel,
                        style,
                        isInModal = false
                    }) => {
    const [showPicker, setShowPicker] = useState(false);
    const buttonRef = useRef(null);
    const [textColor, setTextColor] = useState('#000000');
    const theme = useTheme();

    const handlePlaceholderSelect = (placeholder) => {
        setCurrentDescription(currentDescription + placeholder)
    };

    const applyColorCommand = useCallback((color) => {
        setTextColor(color);
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('foreColor', false, color);
    }, []);

    return (
        <Box sx={{pt: 1}}>
            <EditorProvider>
                <Editor
                    disabled={isFormLive}
                    value={currentDescription}
                    onChange={e => setCurrentDescription(e.target.value)}
                    containerProps={{
                        style: {
                            ...theme.typography.body1,
                            color: theme.palette.text.primary,
                            ...style.editor
                        }
                    }}
                >
                    <Toolbar style={{...style.toolbar}}>
                        <BtnUndo/>
                        <BtnRedo/>
                        <Separator/>
                        <BtnBold/>
                        <BtnItalic/>
                        <BtnUnderline/>
                        <BtnStrikeThrough/>
                        <ToggleColorPickerButton onClick={() => setShowPicker(!showPicker)} ref={buttonRef}
                                                 color={textColor}/>
                        <Separator/>
                        <BtnNumberedList/>
                        <BtnBulletList/>
                        <BtnAlignLeft />
                        <BtnAlignCenter />
                        <BtnAlignRight />
                        <Separator/>
                        <BtnLink/>
                        <BtnClearFormatting/>
                        <Separator/>
                        <BtnStyles/>
                        <Separator/>
                        {showPicker &&
                            <ColorPicker
                                applyColorCommand={applyColorCommand}
                                onClose={() => setShowPicker(false)}
                                anchorEl={buttonRef.current}
                                isInModal={isInModal}
                            />}
                        {accessLevel === FORM_ACCESS_LEVELS.private || accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink
                            ? <PrivatePlaceholderDropdown onChange={(value) => handlePlaceholderSelect(value)}/>
                            : <PublicPlaceholderDropdown onChange={(value) => handlePlaceholderSelect(value)}/>
                        }
                    </Toolbar>
                </Editor>
            </EditorProvider>
        </Box>
    );
};

RichEditor.defaultProps = {
    style: {
        editor: {},
        toolbar: {}
    },
    isInModal: false
};

RichEditor.propTypes = {
    index: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
    currentDescription: PropTypes.string.isRequired,
    setCurrentDescription: PropTypes.func.isRequired,
    isFormLive: PropTypes.bool.isRequired,
    accessLevel: PropTypes.number.isRequired,
    style: PropTypes.object,
    isInModal: PropTypes.bool,
};

export default RichEditor;